import { useEffect, useRef, useState } from 'react';
import PageContainer from '../../../components/container/PageContainer';
import {
  REPORTS_MANAGEMENT_MODAL_FIELDS,
  REPORTSMANAGEMENT_HEADER,
  REPORTSMANAGEMENT_MANAGEMENT_CSV_MODAL_FIELDS,
} from '../../../constants/ReportManagement';
import {
  EmptyReportManagementsGetResponse,
  ReportManagementsArray,
  ReportManagementsGetResponse,
} from '../../../types/reportsManagement/ReportManagementGetResponse';
import { ReportManagementCsvPreviewItem } from '../../../types/reportsManagement/ReportManagementCsvPreviewItem';
import {
  getReportManagementList,
  exportReportManagementsCsv,
  exportReportAccountingCsv,
  updateReportManagement,
  deleteReportManagement,
} from '../../../services/api';
import { Col, Row } from 'react-bootstrap';
import RenderReportManagementRow from './RenderReportManagementRow';
import TablePagination from '../../../components/table/pagination/TablePagination';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  DefaultReportManagementGetQueryParams,
  ReportManagementGetQueryParams,
} from '../../../types/reportsManagement/ReportManagementGetQueryParams';
import { SortOrder } from '../../../types/sort/sort';
import {
  REPORTSMANAGEMENT_DEFAULT_SORT_COLUMN,
  REPORTSMANAGEMENT_DEFAULT_SORT_ORDER,
} from '../../../constants/sort';
import CsvModal from '../../../components/modal/CsvModal';
import ProjectSearch from '../../../components/search/ProjectSearch';
import NameSearch from '../../../components/search/NameSearch';
import YmPicker from '../../../components/picker/YmPicker';
import { format } from 'date-fns';
import { formatYmDisplay } from '../../../utils/formatYmDisplay';
import SearchResetButtons from '../../../components/button/SearchResetButtons';
import CsvExportButton from '../../../components/button/CsvExportButton';
import ReadOnlyTable from '../../../components/table/ReadOnlyTable';
import { saveBlobAsFile } from '../../../utils/saveBlobAsFile';
import SyncedScrollHeader from '../../../components/layout/SyncedScrollHeader';
import '../../../styles/ReportManagementHeader.css';
import {
  EmptyReportManagementPutRequest,
  FormatReportManagementPutRequest,
  formatReportManagementPutRequest,
  ReportManagementPutRequest,
} from '../../../types/reportsManagement/ReportManagementPutRequest';
import SingleAddTable from '../../../components/table/SingleAddTable';
import EditReportManagementRow from './EditReportManagementRow';
import { EmptyReportManagementDeleteItem } from '../../../types/reportsManagement/ReportManagementDeleteItem';
import UpdateModalSave from '../../../components/modal/UpdateModalSave';
import {
  ReportManagementModalView,
  toReportManagementModalView,
} from '../../../types/reportsManagement/ReportManagementModalView';
import useValidation from '../../../hooks/useValidation';
import { BUSINESS_REPORT_VALIDATION } from '../../../constants/validation/reportValidation';
import DeleteModal from '../../../components/modal/DeleteModal';
import { FormattedBusinessReportPutRequest } from '../../../types/businessReport/BusinessReportPutRequest';
import LoadingOverlay from '../../../components/LoadingOverlay';

const ReportManagement = () => {
  const [getQueryParams, setGetQueryParams] =
    useState<ReportManagementGetQueryParams>(
      DefaultReportManagementGetQueryParams
    );
  const [sortOrder, setSortOrder] = useState<SortOrder>(
    REPORTSMANAGEMENT_DEFAULT_SORT_ORDER
  );
  const [activeSortColumn, setActiveSortColumn] = useState<string>(
    REPORTSMANAGEMENT_DEFAULT_SORT_COLUMN
  );
  const [projectLabel, setProjectLabel] = useState<string | null>(null);
  const [csvItem, setCsvItem] = useState<ReportManagementCsvPreviewItem | null>(
    null
  );
  const [showManagementCsvModal, setShowManagementCsvModal] = useState(false);
  const [showAccountingCsvModal, setShowAccountingCsvModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<Date | null>(null);
  const [ymToAdd, setYmToAdd] = useState<Date | null>(null);
  const [controlsKey, setControlsKey] = useState(0);
  const [resetSignal, setResetSignal] = useState(0); // 検索リセット用のシグナル
  // 編集
  const [editingId, setEditingId] = useState<number>(-1);
  const [editingItem, setEditingItem] = useState<ReportManagementPutRequest>(
    EmptyReportManagementPutRequest
  );
  // 削除
  const [deletingItem, setDeletingItem] = useState<ReportManagementsArray>(
    EmptyReportManagementDeleteItem
  );
  // モーダル
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { validate } = useValidation(BUSINESS_REPORT_VALIDATION);
  const queryClient = useQueryClient();
  const fmtYM = (d: Date | null | undefined) => (d ? format(d, 'yyyy-MM') : '');

  // 表の横スクロール参照（ヘッダー追従用）
  const tableRef = useRef<HTMLDivElement>(null);
  // ヘッダーのref（高さ計算用）
  const headerRef = useRef<HTMLDivElement>(null);
  // フッターのref（高さ計算用）
  const footerRef = useRef<HTMLDivElement>(null);

  const { mutateAsync: exportManagementCsv } = useMutation({
    mutationKey: ['exportReportManagementsCsv'],
    mutationFn: () => exportReportManagementsCsv(getQueryParams),
  });

  const { mutateAsync: exportAccountingCsv } = useMutation({
    mutationKey: ['exportReportAccountingCsv'],
    mutationFn: (month?: string) => exportReportAccountingCsv({ month }),
  });

  // 検索のクエリパラメーターをセット
  const setQuery = (
    patch: {
      selectedMonth?: Date | null;
    } & Partial<
      Pick<ReportManagementGetQueryParams, 'projectId' | 'employeeId' | 'month'>
    >
  ) => {
    setGetQueryParams((prev) => {
      const next = {
        ...prev,
        ...patch,
        ...(patch.selectedMonth !== undefined
          ? { month: fmtYM(patch.selectedMonth) }
          : {}),
      } as ReportManagementGetQueryParams;

      return next;
    });
  };

  // 一覧取得
  const {
    data: ReportManagementGetResponse,
    isError,
    error,
    isLoading,
  } = useQuery<ReportManagementsGetResponse>({
    queryKey: ['ReportManagementList'],
    queryFn: () => getReportManagementList(getQueryParams),
    initialData: EmptyReportManagementsGetResponse,
  });

  // 更新
  const editRequest = useMutation({
    mutationFn: (data: FormatReportManagementPutRequest) =>
      updateReportManagement(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ReportManagementList'] });
      setEditingId(-1);
      setEditingItem(EmptyReportManagementPutRequest);
    },
    onError: (error: any) => alert(error.response.data.message),
  });

  // 削除
  const delRequest = useMutation({
    mutationFn: (reportId: number) => deleteReportManagement(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ReportManagementList'] });
      setDeletingItem(EmptyReportManagementDeleteItem);
    },
    onError: (error: any) => alert(error.response.data.message),
  });

  // ページ数、ソート順、ソートカラムが変わったら一覧を再取得
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ['ReportManagementList'],
    });
  }, [getQueryParams.page, getQueryParams.sortKey, getQueryParams.sortOrder]);

  // 一覧取得失敗時のアラート
  useEffect(() => {
    if (isError) {
      alert(error);
    }
  }, [isError]);

  // ソートの状態を管理
  const handleSort = (sortKey: string) => {
    if (sortKey === activeSortColumn) {
      // 選択中のカラムが現在のアクティブなカラムと同じ場合、ソート順を切り替える
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      // 新しいカラムが選択された場合、アクティブなカラムを降順で更新
      setActiveSortColumn(sortKey);
      setSortOrder('desc');
    }
  };

  // ソートの状態が変わったときにクエリパラメータを更新
  useEffect(() => {
    setGetQueryParams((prev) => ({
      ...prev,
      sortOrder: sortOrder,
      sortKey: activeSortColumn,
    }));
  }, [sortOrder, activeSortColumn]);

  // 編集ボタン押下時
  const onEditButtonClick = (item: ReportManagementsArray) => {
    setEditingId(item.id);
    // 編集用のデータをセット
    setEditingItem({
      ...item,
    });
  };

  // 編集時
  const handleEditItemChange = (
    key: keyof ReportManagementPutRequest,
    value: string | number | boolean
  ) => {
    setEditingItem((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // 保存押下 → 確認モーダル
  const onEditSubmitButtonClick = () => {
    setShowUpdateModal(true);
  };

  // 編集キャンセル押下時
  const onEditCancelButtonClick = () => {
    setEditingId(-1);
    setEditingItem(EmptyReportManagementPutRequest);
  };

  // モーダルの保存押下時
  const onEditModalSave = () => {
    const formattedItem = formatReportManagementPutRequest(editingItem);
    const validationError = validate(formattedItem);
    if (validationError) {
      alert(validationError);
      return;
    }
    editRequest.mutate(formattedItem);
    // モーダルを閉じる
    setShowUpdateModal(false);
  };

  // 削除ボタン押下時
  const onDeleteButtonClick = (item: ReportManagementsArray) => {
    // 削除用のデータをセット
    setDeletingItem(item);
    setShowDeleteModal(true);
  };

  // CSV出力用のモーダル情報を作成
  const buildCsvItem = (): ReportManagementCsvPreviewItem => {
    const monthDisp = formatYmDisplay(getQueryParams.month) || '全期間';

    const projectName =
      getQueryParams.projectId != null
        ? (projectLabel ?? '')
        : '全プロジェクト';

    return { projectName, month: monthDisp };
  };

  const onCsvButtonManagementClick = async () => {
    // 期間が選択されていない場合はアラートを表示して処理を中断
    if (!getQueryParams.month || getQueryParams.month.trim() === '') {
      alert('期間を選択してください');
      return;
    }
    setCsvItem(buildCsvItem());
    setShowManagementCsvModal(true);
  };

  const onCsvButtonAccountingClick = async () => {
    setShowAccountingCsvModal(true);
  };

  const onManagementCsvModalSubmitButtonClick = async () => {
    try {
      const { blob, filename } = await exportManagementCsv();

      const fallback = '業務報告一覧_管理用.csv';
      const resolved =
        filename && filename.trim() ? decodeURIComponent(filename) : fallback;

      const ok = await saveBlobAsFile(blob, resolved);
      if (ok) alert('CSVファイルの保存が完了しました');
    } catch (err: any) {
      let msg = '';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        const text = await data.text();
        const json = JSON.parse(text);
        if (json?.message) msg = json.message;
      }
      alert(msg);
    }
  };

  const handleModalOkButtonClick = async () => {
    if (ymToAdd === null) {
      alert('期間を選択してください');
      return;
    }
    try {
      const month = fmtYM(ymToAdd);
      const { blob, filename } = await exportAccountingCsv(month);

      const fallback = '業務報告一覧_経理用.csv';
      const resolved =
        filename && filename.trim() ? decodeURIComponent(filename) : fallback;

      const ok = await saveBlobAsFile(blob, resolved);
      if (ok) alert('CSVファイルの保存が完了しました');
    } catch (err: any) {
      let msg = '';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        const text = await data.text();
        const json = JSON.parse(text);
        if (json?.message) msg = json.message;
      }
      alert(msg);
    } finally {
      setShowAccountingCsvModal(false);
      setYmToAdd(null);
    }
  };

  const isMutating = [editRequest, delRequest].some(
    (m) => m.status === 'pending'
  );

  return (
    <PageContainer title="業務報告管理">
      <LoadingOverlay isLoading={isLoading} isMutating={isMutating} />
      <SyncedScrollHeader
        targetRef={tableRef}
        className="mb-3"
        outerRef={headerRef}
      >
        <Row className="header-row-reportmanagement gx-0 align-items-start">
          {/* 左：検索条件（縮めない） */}
          <Col className="header-fields-reportmanagement order-1">
            <YmPicker
              placeholder="期間を選択"
              selectedMonth={selectedMonth}
              setSelectedMonth={(d) => {
                setSelectedMonth(d);
                setQuery({ selectedMonth: d });
              }}
            />
            <NameSearch
              key={`employee-${controlsKey}`}
              setSelectedId={(e: number) => setQuery({ employeeId: e })}
              resetSignal={resetSignal}
            />
            <ProjectSearch
              key={`project-${controlsKey}`}
              setSelectedId={(p: number) => setQuery({ projectId: p })}
              onSelectedLabel={(label) => setProjectLabel(label)}
              resetSignal={resetSignal}
            />
          </Col>

          {/* 右：ボタン群（検索/リセットは常に横並び、CSV系は折り返し対象） */}
          <Col xs="auto" className="header-right-reportmanagement order-2">
            <div className="actions-group-reportmanagement">
              <div className="actions-searchreset-reportmanagement">
                <SearchResetButtons
                  onSearch={async () => {
                    // 検索ボタン押下時はページを1に戻す
                    setGetQueryParams((prev) => ({ ...prev, page: 1 }));
                    const paramsForSearch = {
                      ...getQueryParams,
                      page: 1,
                    };
                    const data = await queryClient.fetchQuery({
                      queryKey: ['ReportManagementList'],
                      queryFn: () => getReportManagementList(paramsForSearch),
                    });
                    if (!data.items || data.items.length === 0) {
                      alert('該当データが存在しません');
                    }
                  }}
                  onReset={() => {
                    setSelectedMonth(null);
                    setProjectLabel(null);
                    setGetQueryParams(DefaultReportManagementGetQueryParams);
                    setControlsKey((k) => k + 1);
                    setResetSignal((s) => s + 1);
                    queryClient.fetchQuery({
                      queryKey: ['ReportManagementList'],
                      queryFn: () =>
                        getReportManagementList(
                          DefaultReportManagementGetQueryParams
                        ),
                    });
                  }}
                />
              </div>
              <div className="actions-extra-reportmanagement">
                <CsvExportButton
                  onClick={onCsvButtonManagementClick}
                  buttonLabel="管理用CSV出力"
                />
                <CsvExportButton
                  onClick={onCsvButtonAccountingClick}
                  buttonLabel="経理用CSV出力"
                />
              </div>
            </div>
          </Col>
        </Row>
      </SyncedScrollHeader>

      <SingleAddTable<ReportManagementsArray>
        headers={REPORTSMANAGEMENT_HEADER}
        items={ReportManagementGetResponse.items}
        renderItem={(item) => (
          <RenderReportManagementRow
            item={item}
            onEdit={onEditButtonClick}
            onDelete={onDeleteButtonClick}
          />
        )}
        editItem={() => (
          <EditReportManagementRow
            item={editingItem}
            onChange={handleEditItemChange}
            onSubmit={onEditSubmitButtonClick}
            onCancel={onEditCancelButtonClick}
          />
        )}
        editingId={editingId}
        onSort={handleSort}
        sortOrder={sortOrder}
        activeSortColumn={activeSortColumn}
        actionable={false}
        scrollContainerRef={tableRef}
        headerRef={headerRef}
        footerRef={footerRef}
      />

      <TablePagination
        totalPages={ReportManagementGetResponse.totalPages}
        onPageChange={(page) =>
          setGetQueryParams((prev) => ({ ...prev, page }))
        }
        ref={footerRef}
      />

      {showAccountingCsvModal && (
        <CsvModal
          show={showAccountingCsvModal}
          onClose={() => setShowAccountingCsvModal(false)}
          onConfirm={() => handleModalOkButtonClick()}
          modalTitle="期間を選択してCSV出力する"
          saveButtonLabel="OK"
        >
          <div className="justify-content-center d-flex mt-5 mb-5">
            <YmPicker
              placeholder="期間を選択"
              selectedMonth={ymToAdd}
              setSelectedMonth={(d) => setYmToAdd(d)}
            />
          </div>
        </CsvModal>
      )}
      {showUpdateModal && (
        <UpdateModalSave<ReportManagementModalView>
          show={showUpdateModal}
          item={toReportManagementModalView(editingItem)}
          fields={REPORTS_MANAGEMENT_MODAL_FIELDS}
          onClose={() => setShowUpdateModal(false)}
          onSave={onEditModalSave}
          scrollable={true}
        />
      )}
      {showDeleteModal && (
        <DeleteModal<ReportManagementModalView>
          show={showDeleteModal}
          item={toReportManagementModalView(deletingItem)}
          fields={REPORTS_MANAGEMENT_MODAL_FIELDS}
          onClose={() => setShowDeleteModal(false)}
          onDelete={() => {
            delRequest.mutate(deletingItem.id);
            setShowDeleteModal(false);
          }}
          scrollable={true}
        />
      )}
      {showManagementCsvModal && csvItem && (
        <CsvModal<ReportManagementCsvPreviewItem>
          show={showManagementCsvModal}
          item={csvItem}
          fields={REPORTSMANAGEMENT_MANAGEMENT_CSV_MODAL_FIELDS}
          saveButtonLabel="OK"
          onClose={() => {
            setShowManagementCsvModal(false);
            setCsvItem(null);
          }}
          onConfirm={async () => {
            await onManagementCsvModalSubmitButtonClick();
            setShowManagementCsvModal(false);
            setCsvItem(null);
          }}
        />
      )}
    </PageContainer>
  );
};

export default ReportManagement;
